/**
 * Share-trip button. Builds a URL that encodes start, end, stops, and travel
 * mode as query params, then either:
 *   - calls navigator.share (OS share sheet on mobile / supported browsers), or
 *   - falls back to a popover with Copy / Email / SMS / WhatsApp options.
 *
 * Recipients open the link → Index.tsx parses the params on mount and
 * preloads the same trip so they can hit "Go" together.
 */
import { useState } from "react";
import { Share2, Copy, Check, Mail, MessageSquare } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import type { TripPoint } from "@/components/TripSearch";
import type { TravelMode } from "@/api/routing";

interface Props {
  start: TripPoint | null;
  end: TripPoint | null;
  stops: TripPoint[];
  mode: TravelMode;
  className?: string;
}

/** Encode a TripPoint as "lng,lat|name". */
function enc(p: TripPoint): string {
  return `${p.coords[0].toFixed(5)},${p.coords[1].toFixed(5)}|${p.name}`;
}

// A shared trip link must be a real, public web address. We must NOT use
// `window.location.origin` — inside the native iOS app that is
// `capacitor://localhost`, which is a dead link for anyone who receives it
// (it only resolves inside this app's own WebView). pleasantroutes.com
// opens the trip on the web for any recipient, and deep-links straight into
// the app for recipients who have it installed (see useDeepLinks.ts).
const SHARE_ORIGIN = "https://pleasantroutes.com/app";

export function buildShareUrl({
  start,
  end,
  stops,
  mode,
}: {
  start: TripPoint | null;
  end: TripPoint | null;
  stops: TripPoint[];
  mode: TravelMode;
}): string {
  const params = new URLSearchParams();
  if (start) params.set("start", enc(start));
  if (end) params.set("end", enc(end));
  if (stops.length > 0) params.set("stops", stops.map(enc).join(";"));
  params.set("mode", mode);
  return `${SHARE_ORIGIN}?${params.toString()}`;
}

export function ShareTripButton({ start, end, stops, mode, className }: Props) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!end) return null;

  const url = buildShareUrl({ start, end, stops, mode });
  const where = end.name;
  const verb = mode === "driving" ? "drive" : mode === "biking" ? "bike ride" : "walk";
  const title = `My new trip on Pleasant`;
  const text = `Check out my new trip with Pleasant — a ${verb} to ${where}. Open it here:`;

  /** Try to fetch the share-preview image as a File so it rides along with
   *  the OS share sheet (iMessage, WhatsApp, etc. on supported browsers).
   *  Returns null when file-sharing isn't supported. */
  const buildShareFiles = async (): Promise<File[] | null> => {
    try {
      const res = await fetch("/share-trip-og.jpg");
      if (!res.ok) return null;
      const blob = await res.blob();
      const file = new File([blob], "pleasant-trip.jpg", { type: blob.type || "image/jpeg" });
      const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
      if (typeof nav.canShare === "function" && nav.canShare({ files: [file] })) {
        return [file];
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleClick = async () => {
    // Prefer native share sheet on supported browsers (mobile, Safari, Edge).
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        const files = await buildShareFiles();
        await navigator.share(files ? { title, text, url, files } : { title, text, url });
        return;
      } catch {
        // User cancelled or share failed — fall through to popover.
      }
    }
    setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Trip link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          className={
            "h-11 px-4 rounded-full border border-border bg-card text-sm font-medium hover:shadow-soft transition-smooth flex items-center justify-center gap-1.5 " +
            (className ?? "")
          }
          aria-label="Share trip and invite friends"
        >
          <Share2 className="w-4 h-4" />
          Share trip
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="center" className="w-80 p-3">
        <div className="text-sm font-semibold mb-1">Invite friends on this trip</div>
        <p className="text-xs text-muted-foreground mb-3">
          They'll open the same start, destination, and stops in Pleasant Routes.
        </p>

        <div className="flex items-center gap-1.5 mb-3 rounded-lg border border-border bg-muted/40 px-2 py-1.5">
          <input
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 bg-transparent text-[11px] text-muted-foreground truncate outline-none"
          />
          <button
            type="button"
            onClick={copy}
            className="shrink-0 h-7 px-2 rounded-md bg-foreground text-background text-[11px] font-semibold flex items-center gap-1 hover:opacity-90"
          >
            {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1.5">
          <ShareLink
            href={`sms:?&body=${encodeURIComponent(`${text} ${url}`)}`}
            icon={MessageSquare}
            label="Text"
          />
          <ShareLink
            href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`}
            icon={Mail}
            label="Email"
          />
          <ShareLink
            href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`}
            icon={Share2}
            label="WhatsApp"
            external
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ShareLink({
  href,
  icon: Icon,
  label,
  external,
}: {
  href: string;
  icon: typeof Share2;
  label: string;
  external?: boolean;
}) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="h-14 rounded-lg border border-border bg-card hover:bg-muted transition-smooth flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium"
    >
      <Icon className="w-4 h-4 text-pleasant" />
      {label}
    </a>
  );
}

/**
 * Build a relative /app link for a trip, in exactly the format
 * `parseTripPointParam` / `parseStopsParam` read back.
 *
 * Kept next to the parsers on purpose: the two have to agree, and the one time
 * they didn't, a user who tapped save while signed out came back from sign-in
 * to a blank map with the trip they had just planned gone.
 */
export function buildTripPath(args: {
  destination: TripPoint;
  start?: TripPoint | null;
  stops?: TripPoint[];
  mode: string;
}): string {
  const pt = (p: TripPoint) =>
    `${p.coords[0]},${p.coords[1]}|${encodeURIComponent(p.name ?? p.place ?? "")}`;
  const params = [`end=${pt(args.destination)}`];
  if (args.start) params.push(`start=${pt(args.start)}`);
  if (args.stops?.length) params.push(`stops=${args.stops.map(pt).join(";")}`);
  params.push(`mode=${args.mode}`);
  return `/app?${params.join("&")}`;
}

/** Parse a TripPoint from "lng,lat|name". Returns null on bad input. */
export function parseTripPointParam(raw: string | null): TripPoint | null {
  if (!raw) return null;
  const [coordPart, ...nameParts] = raw.split("|");
  const [lngS, latS] = (coordPart ?? "").split(",");
  const lng = Number(lngS);
  const lat = Number(latS);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return null;
  const name = (nameParts.join("|") || "Shared location").slice(0, 200);
  return { name, place: name, coords: [lng, lat] };
}

/** Parse a "stops" param (semicolon-separated TripPoints). */
export function parseStopsParam(raw: string | null): TripPoint[] {
  if (!raw) return [];
  return raw
    .split(";")
    .map((s) => parseTripPointParam(s))
    .filter((p): p is TripPoint => p !== null)
    .slice(0, 10);
}
